import type { AccessoryLink, StockItem } from '@/types/oscar';
import { stockReading, type StockReading } from './deviceStock';

/**
 * What the Hardware catalogue says about the stock behind each ACCESSORY (oscar
 * migration 124) - the twin of {@link ./deviceStock}.
 *
 * Anthony, 15/10/2026: *"worked - now do accessories stock link."* A requested
 * device could be met from the shelf because a stock line was linked to it; a
 * requested accessory could not.
 *
 * ⛔ DERIVED FROM THE LINKS, NOT FROM THE STOCK LINE. A device line carries the
 * devices it answers (`StockItem.Devices`), so the catalogue could group on it.
 * An accessory link is its own table and arrives as its own list of pairs, so
 * every function here takes the pairs. That is deliberate: it leaves the stock
 * list's ordinal-read shape untouched.
 *
 * ⚠ AN ACCESSORY HAS NO COMPANY-WIDE THRESHOLD. A device is Low on the company
 * total against the DEVICE's own "hold at least" (080); an accessory is Low on
 * its own line's `MinQty`, as it always was. So these functions report what is on
 * the shelf and take Low from the lines (the server's answer, `IsLow`), and the
 * panel shows no "hold at least" for an accessory.
 */

const answeredBy = (accessoryOptionId: number, links: AccessoryLink[]) =>
  new Set(links.filter((l) => l.AccessoryOptionId === accessoryOptionId).map((l) => l.StockItemId));

/** The stock lines that answer one accessory, by name. */
export function accessoryLinesFor(
  accessoryOptionId: number,
  items: StockItem[],
  links: AccessoryLink[],
): StockItem[] {
  const ids = answeredBy(accessoryOptionId, links);

  return items
    .filter((i) => ids.has(i.StockItemId))
    .sort((a, b) => a.Name.localeCompare(b.Name));
}

/**
 * Stock lines that could be linked to this accessory.
 *
 * ⛔ ACCESSORY-TYPE LINES ONLY, as the server insists. A device line answers
 * devices and a consumable is used up rather than given - offering one here would
 * be offering something the save will refuse.
 *
 * ⚠ LINES THAT ANSWER NOTHING COME FIRST, as they do for devices: those are the
 * ones that can never be given to a request from stock, so they are what somebody
 * opening this list is most likely to be looking for. A line that already answers
 * a DIFFERENT accessory is still offered - one line may stand behind several (one
 * "Case - Standard" line for several phone cases).
 */
export function linkableAccessoryLines(
  accessoryOptionId: number,
  items: StockItem[],
  links: AccessoryLink[],
): StockItem[] {
  const already = answeredBy(accessoryOptionId, links);
  const anywhere = new Set(links.map((l) => l.StockItemId));

  return items
    .filter((i) => i.ItemType === 'Accessory' && !already.has(i.StockItemId))
    .sort((a, b) => {
      const aNone = anywhere.has(a.StockItemId) ? 1 : 0;
      const bNone = anywhere.has(b.StockItemId) ? 1 : 0;
      return aNone - bNone || a.Name.localeCompare(b.Name);
    });
}

/**
 * The names of the accessories one stock line answers - for the picker and for the
 * "this line will answer nothing" warning.
 */
export function accessoryNamesFor(
  stockItemId: number,
  links: AccessoryLink[],
  names: ReadonlyMap<number, string>,
): string[] {
  return links
    .filter((l) => l.StockItemId === stockItemId)
    .map((l) => names.get(l.AccessoryOptionId))
    .filter((n): n is string => !!n)
    .sort((a, b) => a.localeCompare(b));
}

/**
 * One reading of an accessory's stock, in the same words a device gets - so "Not
 * stocked" means one thing on both lists, and means "bought per request", not a
 * fault.
 *
 * ⚠ LOW IS THE SERVER'S ANSWER, from the lines (`IsLow`, each against its own
 * `MinQty`). Recomputing it here would make this screen and the Stock page
 * disagree.
 */
export function accessoryReading(lines: StockItem[]): StockReading {
  return stockReading({
    StockLines: lines.length,
    TotalOnHand: lines.reduce((sum, l) => sum + l.QtyOnHand, 0),
    IsLow: lines.some((l) => l.IsLow),
    IsAccessoryOnly: false,
  });
}
